import { useEffect, type ReactNode } from 'react'
import { useAuthStore, useVerticalModule } from '@lipaflow/shared'
import { getOperatorPortalUrl } from '../lib/operatorPortalUrls'

/**
 * Sends an operator to the portal their organisation actually belongs to.
 *
 * Splitting one B2B app into two deployments creates a failure mode the
 * monolith did not have: a school bursar who has bookmarked the property
 * portal, or follows a stale link, authenticates fine and then sees a shell
 * with none of their pages — because this deployment composes the wrong
 * vertical.
 *
 * So when the tenant's `vertical_code` disagrees with the composed module's
 * `code`, redirect rather than render. The check is deliberately conservative:
 * it stays silent until the tenant is loaded and both codes are known, so a
 * slow `/auth/me` never bounces anyone.
 */
export function VerticalMismatchGuard({ children }: { children: ReactNode }) {
  const tenant = useAuthStore((s) => s.tenant)
  const module = useVerticalModule()

  const tenantVertical = tenant?.vertical_code
  const shellVertical = module?.code
  const mismatched = Boolean(tenantVertical && shellVertical && tenantVertical !== shellVertical)

  useEffect(() => {
    if (!mismatched || !tenantVertical) return
    const target = getOperatorPortalUrl(tenantVertical)
    if (!target) return // Unknown vertical: better to render than to bounce nowhere.
    window.location.replace(`${target}${window.location.pathname}${window.location.search}`)
  }, [mismatched, tenantVertical])

  if (mismatched) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas px-4">
        <div className="text-center max-w-sm">
          <p className="font-outfit font-semibold text-ink">Taking you to your portal…</p>
          <p className="mt-2 font-inter text-body-sm text-ink-muted">
            This account belongs to a different LipaFlow portal. Redirecting now.
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export default VerticalMismatchGuard
